import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  MapPin,
  Calendar,
  Clock,
  FileText,
  Download,
  Truck,
  X,
  User,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { logisticApi, modelsApi } from "@/lib/unifiedApi";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, addMonths, subMonths, parseISO } from "date-fns";
import { th } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

// ─── helpers ───────────────────────────────────────────────────────────────
const thMonth = (date) =>
  format(date, "MMMM yyyy", { locale: th });

const fmtPrice = (n) =>
  Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 0 });

const WITHHOLDING_RATE = 0.01;

const EMPTY_FORM = {
  job_name: "",
  model_id: "",
  model_name: "",
  model_image_url: "",
  date: format(new Date(), "yyyy-MM-dd"),
  time_start: "09:00",
  time_end: "17:00",
  pickup_address: "",
  pickup_maps: "",
  dropoff_address: "",
  dropoff_maps: "",
  price: "",
  note: "",
};

// ─── Model avatar (square) ─────────────────────────────────────────────────
function ModelAvatar({ imageUrl, name, size = 34 }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: imageUrl ? "transparent" : "#E5E7EB",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.32,
        fontWeight: 500,
        color: "#6B7280",
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initials
      )}
    </div>
  );
}

// ─── PDF print helper ──────────────────────────────────────────────────────
function printPdf({ trips, month, docNumber }) {
  const total = trips.reduce((s, t) => s + Number(t.price || 0), 0);
  const wht = Math.round(total * WITHHOLDING_RATE);
  const net = total - wht;

  // Collect unique models for the chips row
  const modelMap = {};
  trips.forEach((t) => {
    const key = t.model_id || t.model_name;
    if (key && !modelMap[key]) {
      modelMap[key] = { name: t.model_name, imageUrl: t.model_image_url };
    }
  });
  const uniqueModels = Object.values(modelMap);

  // chip colors per model index
  const chipColors = ["#BFCEFF","#FDE68A","#BBF7D0","#FECACA","#DDD6FE","#FED7AA"];
  const chipTextColors = ["#1E3A8A","#78350F","#14532D","#7F1D1D","#4C1D95","#7C2D12"];
  let chipIdx = 0;
  const modelColorMap = {};
  uniqueModels.forEach((m) => {
    const key = m.name;
    if (!modelColorMap[key]) {
      modelColorMap[key] = { bg: chipColors[chipIdx % chipColors.length], text: chipTextColors[chipIdx % chipTextColors.length] };
      chipIdx++;
    }
  });

  const initials2 = (name) => (name || "?").split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  const pdfExtractLatLng = (url) => {
    if (!url) return null;
    let m = url.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    m = url.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    m = url.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    return null;
  };
  const pdfCalcKm = (urlA, urlB) => {
    const a = pdfExtractLatLng(urlA);
    const b = pdfExtractLatLng(urlB);
    if (!a || !b) return null;
    const R = 6371;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.asin(Math.sqrt(h)) * 1.4;
  };

  const rows = trips
    .map(
      (t, i) => {
        const mc = modelColorMap[t.model_name] || { bg: "#E5E7EB", text: "#374151" };
        const km = pdfCalcKm(t.pickup_maps, t.dropoff_maps);
        const kmCell = km !== null ? `${km.toFixed(1)} กม.` : `<span style="color:#9CA3AF">—</span>`;
        const price = Number(t.price || 0);
        const avgCell = (km !== null && km > 0)
          ? `${(price / km).toFixed(2)}`
          : `<span style="color:#9CA3AF">—</span>`;
        return `
        <tr style="background:${i % 2 === 0 ? "#fff" : "#FAFAFA"}">
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;text-align:center;color:#6B7280">${i + 1}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB">
            <span style="display:inline-flex;align-items:center;gap:5px">
              <span style="width:22px;height:22px;border-radius:4px;background:${mc.bg};color:${mc.text};display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;flex-shrink:0">${initials2(t.model_name)}</span>
              <span style="font-size:12px;font-weight:500">${t.model_name || "-"}</span>
            </span>
          </td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:12px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${t.job_name || "-"}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;white-space:nowrap">${t.date ? t.date.replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${parseInt(d)} ${["","ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."][parseInt(m)]}`) : ""} ${t.time_start || ""}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;overflow:hidden;text-overflow:ellipsis">${t.pickup_address || "-"}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;white-space:nowrap">${t.date ? t.date.replace(/(\d{4})-(\d{2})-(\d{2})/, (_, y, m, d) => `${parseInt(d)} ${["","ม.ค.","ก.พ.","มี.ค.","เม.ย.","พ.ค.","มิ.ย.","ก.ค.","ส.ค.","ก.ย.","ต.ค.","พ.ย.","ธ.ค."][parseInt(m)]}`) : ""} ${t.time_end || ""}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;overflow:hidden;text-overflow:ellipsis">${t.dropoff_address || "-"}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;text-align:right;color:#374151">${kmCell}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;font-size:11px;text-align:right;color:#374151">${avgCell}</td>
          <td style="padding:7px 8px;border:0.5px solid #E5E7EB;text-align:right;font-weight:600;font-size:12px">${fmtPrice(t.price)}</td>
        </tr>`;
      }
    )
    .join("");

  const modelChips = uniqueModels
    .map((m) => {
      const mc = modelColorMap[m.name] || { bg: "#E5E7EB", text: "#374151" };
      return `<span style="display:inline-flex;align-items:center;gap:6px;background:${mc.bg};border-radius:6px;padding:4px 10px 4px 6px;margin:2px">
        <span style="width:20px;height:20px;border-radius:4px;background:rgba(0,0,0,0.12);display:inline-flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;color:${mc.text}">${initials2(m.name)}</span>
        <span style="font-size:11px;font-weight:600;color:${mc.text}">${m.name}</span>
      </span>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8"/>
<title>ใบสรุปงบประมาณขนส่ง ${docNumber}</title>
<style>
  @page { margin: 15mm 12mm; size: A4 landscape; }
  body { font-family: 'Sarabun', 'Tahoma', sans-serif; color: #111827; font-size: 12px; margin: 0; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th { background: #111827; padding: 7px 8px; text-align: left; font-size: 11px; font-weight: 600; color: #FFFFFF; overflow: hidden; }
  th:last-child { text-align: right; }
  .toolbar { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; margin-bottom: 18px; border-radius: 8px; flex-wrap: wrap; }
  .toolbar label { font-size: 11px; color: #64748B; display: flex; align-items: center; gap: 6px; }
  .toolbar input { border: 1px solid #CBD5E1; border-radius: 4px; padding: 4px 8px; font-size: 11px; font-family: inherit; color: #111827; width: 160px; }
  .toolbar input:focus { outline: none; border-color: #111827; }
  .save-btn { background: #111827; color: #fff; border: none; border-radius: 6px; padding: 7px 18px; font-size: 12px; font-family: inherit; cursor: pointer; font-weight: 600; white-space: nowrap; }
  .save-btn:hover { background: #374151; }
  @media print { .toolbar { display: none !important; } }
</style>
</head>
<body>

<div class="toolbar">
  <label>ชื่อบัญชี <input id="acct-name" value="นางสาว พัชรี พินิจพัฒนะพรชัย" oninput="updateBank()"></label>
  <label>ธนาคาร <input id="bank-name" value="PromptPay" style="width:110px" oninput="updateBank()"></label>
  <label>เลขที่บัญชี <input id="acct-no" value="0617471221" style="width:130px" oninput="updateBank()"></label>
  <button class="save-btn" onclick="window.print()">บันทึก PDF</button>
</div>


<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px">
  <div style="display:flex;align-items:center;gap:12px">
    <img src="${window.location.origin}/LOGO_FYM-01.svg" alt="FYM" style="height:52px;width:auto" onerror="this.style.display='none'">
    <div>
      <div style="font-size:17px;font-weight:800;letter-spacing:0.3px">FLASHYOURMEME CO., LTD.</div>
      <div style="font-size:12px;font-weight:600;color:#374151;margin-top:1px">บริษัท แฟลชยัวร์มีม จำกัด</div>
      <div style="font-size:10px;color:#6B7280;margin-top:2px">เลขประจำตัวผู้เสียภาษี: 0145565003261</div>
      <div style="font-size:10px;color:#6B7280;margin-top:1px">628/164 ถนนเลียบคลองรังสิต ตำบลประชาธิปัตย์ อำเภอธัญบุรี จ.ปทุมธานี 12130</div>
    </div>
  </div>
  <div style="text-align:right;font-size:10px;color:#6B7280;line-height:2">
    <div style="font-size:11px;font-weight:700;color:#111827">เอกสารเลขที่</div>
    <div style="font-size:14px;font-weight:800;color:#111827;letter-spacing:0.5px">${docNumber}</div>
    <div>${format(new Date(), "d MMM yyyy", { locale: th })}</div>
  </div>
</div>

<div style="border-top:2px solid #111827;border-bottom:1px solid #D1D5DB;padding:10px 0;margin-bottom:14px;text-align:center">
  <div style="font-size:15px;font-weight:700">ใบสรุปงบประมาณขนส่งประจำเดือน — ${thMonth(month)}</div>
</div>

<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px">
  ${modelChips || '<span style="font-size:11px;color:#9CA3AF">ไม่มีข้อมูลโมเดล</span>'}
  <span style="font-size:11px;color:#6B7280;margin-left:4px">รวม ${trips.length} เที่ยว</span>
</div>

<table style="margin-bottom:20px">
  <colgroup>
    <col style="width:24px">
    <col style="width:96px">
    <col style="width:124px">
    <col style="width:84px">
    <col style="width:106px">
    <col style="width:84px">
    <col style="width:106px">
    <col style="width:52px">
    <col style="width:60px">
    <col style="width:56px">
  </colgroup>
  <tr>
    <th>#</th>
    <th>โมเดล</th>
    <th>ชื่องาน</th>
    <th>รับ</th>
    <th>สถานที่รับ</th>
    <th>ส่ง</th>
    <th>สถานที่ส่ง</th>
    <th style="text-align:right">กม.</th>
    <th style="text-align:right">เฉลี่ย<br>฿/กม.</th>
    <th style="text-align:right">฿</th>
  </tr>
  ${rows}
</table>

<div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:24px;gap:20px">
  <div id="bank-display" style="flex:1;border:1px solid #E5E7EB;border-radius:6px;padding:8px 12px;font-size:10px;color:#6B7280;line-height:2">
    <strong style="color:#111827;font-size:11px">ข้อมูลการโอนเงิน</strong> &nbsp;·&nbsp;
    ชื่อบัญชี: <span id="disp-name">นางสาว พัชรี พินิจพัฒนะพรชัย</span> &nbsp;|&nbsp;
    ธนาคาร: <span id="disp-bank">PromptPay</span> &nbsp;|&nbsp;
    เลขที่: <span id="disp-no">0617471221</span>
  </div>
  <div style="min-width:220px">
    <div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;border-bottom:0.5px solid #F3F4F6">
      <span style="color:#374151">ยอดรวม</span><span style="font-weight:500">${fmtPrice(total)}</span>
    </div>
    <div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;color:#DC2626">
      <span>หัก ณ ที่จ่าย 1%</span><span>(${fmtPrice(wht)})</span>
    </div>
    <div style="display:flex;justify-content:space-between;font-size:15px;font-weight:800;padding:6px 0;border-top:1.5px solid #111827;margin-top:2px">
      <span>ยอดสุทธิ</span><span>${fmtPrice(net)}</span>
    </div>
  </div>
</div>

<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:32px;margin-top:32px;text-align:center">
  <div>
    <div style="height:48px"></div>
    <div style="border-top:1px solid #9CA3AF;padding-top:5px;font-size:11px;color:#374151">ผู้จ่ายเงิน</div>
    <div style="font-size:10px;color:#9CA3AF;margin-top:2px">วันที่ ......./......./.......</div>
  </div>
  <div>
    <div style="height:48px"></div>
    <div style="border-top:1px solid #9CA3AF;padding-top:5px;font-size:11px;color:#374151">ผู้รับเงิน</div>
    <div style="font-size:10px;color:#9CA3AF;margin-top:2px">วันที่ ......./......./.......</div>
  </div>
  <div>
    <div style="height:48px"></div>
    <div style="border-top:1px solid #9CA3AF;padding-top:5px;font-size:11px;color:#374151">ผู้ตรวจสอบ</div>
    <div style="font-size:10px;color:#9CA3AF;margin-top:2px">วันที่ ......./......./.......</div>
  </div>
</div>

<div style="margin-top:32px;border-top:0.5px solid #E5E7EB;padding-top:8px;text-align:center;font-size:9px;color:#9CA3AF">
  FLASHYOURMEME CO., LTD. &nbsp;·&nbsp; ${docNumber} &nbsp;·&nbsp; สร้างโดยระบบอัตโนมัติ
</div>

<script>
function updateBank() {
  document.getElementById('disp-name').textContent = document.getElementById('acct-name').value;
  document.getElementById('disp-bank').textContent = document.getElementById('bank-name').value;
  document.getElementById('disp-no').textContent = document.getElementById('acct-no').value;
}
<\/script>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) {
    toast.error("กรุณาอนุญาต pop-up เพื่อพิมพ์ PDF");
    return;
  }
  win.document.write(html);
  win.document.close();
}

// ─── ETA helpers ───────────────────────────────────────────────────────────
function extractLatLng(url) {
  if (!url) return null;
  // @lat,lng,zoom (most share links)
  let m = url.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  // ?q=lat,lng or &q=lat,lng
  m = url.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  // ll=lat,lng
  m = url.match(/[?&]ll=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  // /place/.../data=...!3d{lat}!4d{lng}
  m = url.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
  if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  return null;
}

function calcEta(pickupUrl, dropoffUrl) {
  const hasLinks = !!(pickupUrl && dropoffUrl);
  const a = extractLatLng(pickupUrl);
  const b = extractLatLng(dropoffUrl);
  if (!a || !b) return { hasLinks, coords: false };
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const roadKm = R * 2 * Math.asin(Math.sqrt(h)) * 1.4;
  const speed = roadKm < 30 ? 30 : roadKm < 80 ? 50 : roadKm < 200 ? 80 : 100;
  const minutes = Math.round(roadKm / speed * 60);
  const h2 = Math.floor(minutes / 60);
  const m2 = minutes % 60;
  const label = h2 > 0 ? `${h2} ชม. ${m2} นาที` : `${m2} นาที`;
  return { hasLinks, coords: true, km: roadKm, minutes, label };
}

// ─── Trip Card ─────────────────────────────────────────────────────────────
function TripCard({ trip, onEdit, onDelete }) {
  const eta = calcEta(trip.pickup_maps, trip.dropoff_maps);

  return (
    <div className="border border-gray-100 rounded-xl bg-white hover:border-gray-200 transition-all overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <ModelAvatar imageUrl={trip.model_image_url} name={trip.model_name} size={32} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{trip.job_name || "ไม่มีชื่องาน"}</p>
          <p className="text-xs text-gray-400">{trip.model_name || "—"}</p>
        </div>
        <span className="text-sm font-bold text-blue-600 whitespace-nowrap">฿{fmtPrice(trip.price)}</span>
        <div className="flex gap-1">
          <button onClick={() => onEdit(trip)} className="w-7 h-7 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center hover:bg-gray-100 transition-colors">
            <Pencil size={12} className="text-gray-400" />
          </button>
          <button onClick={() => onDelete(trip)} className="w-7 h-7 rounded-lg border border-gray-100 bg-gray-50 flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-colors">
            <Trash2 size={12} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Date separator */}
      <div className="flex items-center gap-1.5 px-4 py-1.5 border-t border-b border-gray-100 bg-gray-50">
        <Calendar size={11} className="text-gray-400" />
        <span className="text-xs text-gray-500">{trip.date || "—"}</span>
      </div>

      {/* Route row */}
      <div className="grid px-4 py-3" style={{ gridTemplateColumns: "1fr auto 1fr", gap: "8px", alignItems: "center" }}>
        {/* Pickup */}
        <div>
          <div className="text-base font-bold text-gray-900 leading-tight">{trip.time_start || "—"}</div>
          <div className="flex items-center gap-1 mt-1">
            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
            <span className="text-xs text-gray-500 truncate">{trip.pickup_address || "—"}</span>
            {trip.pickup_maps && (
              <a href={trip.pickup_maps} target="_blank" rel="noreferrer" className="flex-shrink-0 ml-0.5">
                <ExternalLink size={10} className="text-blue-400" />
              </a>
            )}
          </div>
        </div>

        {/* ETA center */}
        <div className="flex flex-col items-center px-2">
          <div className="flex items-center w-full">
            <div className="flex-1 h-px bg-gray-200" />
            <div className="text-gray-300 mx-1" style={{ fontSize: 10 }}>▶</div>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          {eta.coords ? (
            <>
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 mt-1 whitespace-nowrap">{eta.label}</span>
              <span className="text-[10px] text-gray-400 mt-0.5">~{eta.km.toFixed(1)} กม.</span>
            </>
          ) : eta.hasLinks ? (
            <span className="text-[10px] text-gray-400 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 mt-1 whitespace-nowrap">ETA —</span>
          ) : null}
        </div>

        {/* Dropoff */}
        <div className="text-right">
          <div className="text-base font-bold text-gray-900 leading-tight">{trip.time_end || "—"}</div>
          <div className="flex items-center justify-end gap-1 mt-1">
            {trip.dropoff_maps && (
              <a href={trip.dropoff_maps} target="_blank" rel="noreferrer" className="flex-shrink-0">
                <ExternalLink size={10} className="text-blue-400" />
              </a>
            )}
            <span className="text-xs text-gray-500 truncate">{trip.dropoff_address || "—"}</span>
            <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          </div>
        </div>
      </div>

      {trip.note && (
        <div className="px-4 pb-2.5 text-xs text-gray-400 italic border-t border-gray-100 pt-2">{trip.note}</div>
      )}
    </div>
  );
}

// ─── Model Dropdown ────────────────────────────────────────────────────────
function ModelDropdown({ models, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = models.find((m) => m.id === value);
  const isManual = value === "__manual__";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 h-10 border border-[#333] rounded-lg px-3 bg-[#252525] hover:border-[#444] transition-colors text-left"
      >
        {selected ? (
          <>
            <ModelAvatar imageUrl={selected.image_url} name={selected.model_name} size={26} />
            <span className="flex-1 text-sm text-white">{selected.model_name}</span>
          </>
        ) : isManual ? (
          <>
            <div className="w-6 h-6 rounded-md border border-dashed border-gray-600 flex items-center justify-center flex-shrink-0">
              <User size={12} className="text-gray-500" />
            </div>
            <span className="flex-1 text-sm text-gray-400">กรอกชื่อ Manual</span>
          </>
        ) : (
          <span className="flex-1 text-sm text-gray-500">เลือกโมเดล...</span>
        )}
        <ChevronLeft
          size={14}
          className="text-gray-500 flex-shrink-0"
          style={{ transform: open ? "rotate(90deg)" : "rotate(270deg)" }}
        />
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1e1e1e] border border-[#333] rounded-lg shadow-xl overflow-hidden max-h-56 overflow-y-auto">
          {models.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onChange({ model_id: m.id, model_name: m.model_name, model_image_url: m.image_url || "" });
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#2a2a2a] text-left transition-colors"
            >
              <ModelAvatar imageUrl={m.image_url} name={m.model_name} size={28} />
              <div>
                <p className="text-sm font-medium text-white">{m.model_name}</p>
                {m.pc_model && <p className="text-xs text-gray-500">{m.pc_model}</p>}
              </div>
            </button>
          ))}
          <div className="border-t border-[#333]">
            <button
              type="button"
              onClick={() => {
                onChange({ model_id: "__manual__", model_name: "", model_image_url: "" });
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#2a2a2a] text-left transition-colors"
            >
              <div className="w-7 h-7 rounded-md border border-dashed border-gray-600 flex items-center justify-center flex-shrink-0">
                <Plus size={12} className="text-gray-500" />
              </div>
              <span className="text-sm text-gray-400">กรอกชื่อ Manual</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Add / Edit Dialog ─────────────────────────────────────────────────────
function TripDialog({ open, onClose, onSave, models, editingTrip }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editingTrip ? { ...EMPTY_FORM, ...editingTrip } : { ...EMPTY_FORM, date: format(new Date(), "yyyy-MM-dd") });
    }
  }, [open, editingTrip]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleModelChange = ({ model_id, model_name, model_image_url }) => {
    setForm((f) => ({ ...f, model_id, model_name: model_id === "__manual__" ? "" : model_name, model_image_url }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.job_name.trim()) return toast.error("กรุณาระบุชื่องาน");
    if (!form.price || Number(form.price) <= 0) return toast.error("กรุณาระบุราคา");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        month: form.date.slice(0, 7), // YYYY-MM
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      toast.error("บันทึกไม่สำเร็จ: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isManual = form.model_id === "__manual__";

  const inputCls = "h-9 text-sm bg-[#252525] border-[#333] text-white placeholder:text-gray-600 focus:border-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0";
  const nativeInputCls = "h-9 border border-[#333] rounded-lg px-3 text-sm bg-[#252525] text-white focus:outline-none focus:border-gray-500 [color-scheme:dark]";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-[#1a1a1a] border-[#2a2a2a] text-white">
        <DialogHeader>
          <DialogTitle className="text-white">{editingTrip ? "แก้ไขเที่ยวรถ" : "เพิ่มเที่ยวรถ"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">

          {/* Model */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">โมเดล</Label>
            <ModelDropdown
              models={models}
              value={form.model_id}
              onChange={handleModelChange}
            />
            {isManual && (
              <Input
                placeholder="ชื่อโมเดล (manual)"
                value={form.model_name}
                onChange={(e) => set("model_name", e.target.value)}
                className={inputCls}
              />
            )}
          </div>

          {/* Job name */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">ชื่องาน</Label>
            <Input
              placeholder="เช่น งาน TikTok Live Central World"
              value={form.job_name}
              onChange={(e) => set("job_name", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Date + time */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">วันที่และเวลา</Label>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className={`col-span-3 ${nativeInputCls}`}
              />
              <input
                type="time"
                value={form.time_start}
                onChange={(e) => set("time_start", e.target.value)}
                className={`col-span-1 ${nativeInputCls}`}
              />
              <div className="flex items-center justify-center text-xs text-gray-500">ถึง</div>
              <input
                type="time"
                value={form.time_end}
                onChange={(e) => set("time_end", e.target.value)}
                className={`col-span-1 ${nativeInputCls}`}
              />
            </div>
          </div>

          {/* Pickup */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">สถานที่รับ</Label>
            <div className="flex gap-2">
              <Input
                placeholder="ที่อยู่รับ"
                value={form.pickup_address}
                onChange={(e) => set("pickup_address", e.target.value)}
                className={`${inputCls} flex-1`}
              />
              <div className="flex flex-col gap-1 w-28">
                <Input
                  placeholder="Maps URL"
                  value={form.pickup_maps}
                  onChange={(e) => set("pickup_maps", e.target.value)}
                  className={`${inputCls} text-xs ${
                    form.pickup_maps && !extractLatLng(form.pickup_maps)
                      ? "border-amber-400 focus:border-amber-400"
                      : form.pickup_maps && extractLatLng(form.pickup_maps)
                      ? "border-emerald-400"
                      : ""
                  }`}
                />
                {form.pickup_maps && !extractLatLng(form.pickup_maps) && (
                  <p className="text-[10px] text-amber-500 leading-tight">
                    <AlertTriangle size={10} className="inline mr-0.5"/>ใช้ลิ้งค์เต็ม ไม่ใช่ short link
                  </p>
                )}
                {form.pickup_maps && extractLatLng(form.pickup_maps) && (
                  <p className="text-[10px] text-emerald-500 leading-tight"><CheckCircle2 size={10} className="inline mr-0.5"/>อ่านพิกัดได้</p>
                )}
              </div>
            </div>
          </div>

          {/* Dropoff */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">สถานที่ส่ง</Label>
            <div className="flex gap-2">
              <Input
                placeholder="ที่อยู่ส่ง"
                value={form.dropoff_address}
                onChange={(e) => set("dropoff_address", e.target.value)}
                className={`${inputCls} flex-1`}
              />
              <div className="flex flex-col gap-1 w-28">
                <Input
                  placeholder="Maps URL"
                  value={form.dropoff_maps}
                  onChange={(e) => set("dropoff_maps", e.target.value)}
                  className={`${inputCls} text-xs ${
                    form.dropoff_maps && !extractLatLng(form.dropoff_maps)
                      ? "border-amber-400 focus:border-amber-400"
                      : form.dropoff_maps && extractLatLng(form.dropoff_maps)
                      ? "border-emerald-400"
                      : ""
                  }`}
                />
                {form.dropoff_maps && !extractLatLng(form.dropoff_maps) && (
                  <p className="text-[10px] text-amber-500 leading-tight">
                    <AlertTriangle size={10} className="inline mr-0.5"/>ใช้ลิ้งค์เต็ม ไม่ใช่ short link
                  </p>
                )}
                {form.dropoff_maps && extractLatLng(form.dropoff_maps) && (
                  <p className="text-[10px] text-emerald-500 leading-tight"><CheckCircle2 size={10} className="inline mr-0.5"/>อ่านพิกัดได้</p>
                )}
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">ราคา (บาท)</Label>
            <Input
              type="number"
              min="0"
              placeholder="0"
              value={form.price}
              onChange={(e) => set("price", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Note */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-400">หมายเหตุ</Label>
            <Textarea
              placeholder="หมายเหตุเพิ่มเติม"
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              className="text-sm h-20 resize-none bg-[#252525] border-[#333] text-white placeholder:text-gray-600 focus:border-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Footer buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#333] text-gray-300 hover:bg-[#252525] hover:text-white bg-transparent"
            >
              ยกเลิก
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? "กำลังบันทึก..." : "บันทึกเที่ยว"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function LogisticPage() {
  const [trips, setTrips] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Subscribe to trips
  useEffect(() => {
    const unsub = logisticApi.subscribe((data) => {
      setTrips(data);
      setLoading(false);
    });
    const t = setTimeout(() => setLoading(false), 2000);
    return () => { unsub(); clearTimeout(t); };
  }, []);

  // Subscribe to models (for dropdown)
  useEffect(() => {
    const unsub = modelsApi.subscribe((data) => setModels(data));
    return unsub;
  }, []);

  // Filter by current month
  const monthStr = format(currentMonth, "yyyy-MM");
  const monthTrips = useMemo(
    () =>
      trips
        .filter((t) => (t.month || (t.date || "").slice(0, 7)) === monthStr)
        .sort((a, b) => {
          const da = `${a.date || ""}T${a.time_start || "00:00"}`;
          const db = `${b.date || ""}T${b.time_start || "00:00"}`;
          return da.localeCompare(db);
        }),
    [trips, monthStr]
  );

  const total = monthTrips.reduce((s, t) => s + Number(t.price || 0), 0);
  const wht = Math.round(total * WITHHOLDING_RATE);
  const net = total - wht;

  const docNumber = `LOG-${format(currentMonth, "yyyy-MM")}-001`;

  const handleSave = async (payload) => {
    if (editingTrip) {
      await logisticApi.update(editingTrip.id, payload);
      toast.success("แก้ไขเที่ยวเรียบร้อย");
    } else {
      await logisticApi.create(payload);
      toast.success("เพิ่มเที่ยวเรียบร้อย");
    }
    setEditingTrip(null);
  };

  const handleDelete = async (trip) => {
    try {
      await logisticApi.delete(trip.id);
      toast.success("ลบเที่ยวเรียบร้อย");
    } catch {
      toast.error("ลบไม่สำเร็จ");
    }
    setDeleteConfirm(null);
  };

  const openAdd = () => { setEditingTrip(null); setDialogOpen(true); };
  const openEdit = (trip) => { setEditingTrip(trip); setDialogOpen(true); };

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between pl-14 lg:pl-5 pr-4 sm:pr-5 py-3 gap-y-2 bg-white border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Truck size={18} className="text-blue-500" />
          <span className="font-semibold text-gray-800">Logistic</span>

          {/* Month nav */}
          <div className="flex items-center gap-1 ml-3">
            <button
              onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft size={14} className="text-gray-500" />
            </button>
            <span className="text-sm font-medium w-36 text-center">{thMonth(currentMonth)}</span>
            <button
              onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
              className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <ChevronRight size={14} className="text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* PDF button */}
          <button
            onClick={() => printPdf({ trips: monthTrips, month: currentMonth, docNumber })}
            disabled={monthTrips.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={13} />
            PDF / พิมพ์
          </button>

          {/* Add button */}
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={13} />
            เพิ่มเที่ยว
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 px-4 sm:px-5 py-3 flex-shrink-0">
        <div className="bg-white border border-gray-100 rounded-xl p-3">
          <p className="text-xs text-gray-500 mb-1">จำนวนเที่ยว</p>
          <p className="text-xl font-semibold">{monthTrips.length} เที่ยว</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3">
          <p className="text-xs text-gray-500 mb-1">ยอดรวม</p>
          <p className="text-xl font-semibold">฿{fmtPrice(total)}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3">
          <p className="text-xs text-gray-500 mb-1">ยอดสุทธิ (หัก 1%)</p>
          <p className="text-xl font-semibold text-emerald-600">฿{fmtPrice(net)}</p>
          {total > 0 && (
            <p className="text-xs text-red-400 mt-0.5">หัก {fmtPrice(wht)} บาท</p>
          )}
        </div>
      </div>

      {/* ── Trip list ── */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-6">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-blue-500" />
          </div>
        ) : monthTrips.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Truck size={32} className="mb-3 opacity-30" />
            <p className="text-sm">ยังไม่มีเที่ยวรถในเดือนนี้</p>
            <button
              onClick={openAdd}
              className="mt-3 text-xs text-blue-500 hover:underline flex items-center gap-1"
            >
              <Plus size={12} /> เพิ่มเที่ยวแรก
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {monthTrips.map((trip) => (
              <TripCard
                key={trip.id}
                trip={trip}
                onEdit={openEdit}
                onDelete={setDeleteConfirm}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Add/Edit Dialog ── */}
      <TripDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingTrip(null); }}
        onSave={handleSave}
        models={models}
        editingTrip={editingTrip}
      />

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm bg-[#1a1a1a] border-[#2a2a2a] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">ยืนยันการลบ</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400 py-2">
            ลบเที่ยว <strong className="text-white">"{deleteConfirm?.job_name}"</strong> ออกจากระบบ?
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 border-[#333] text-gray-300 hover:bg-[#252525] hover:text-white bg-transparent"
            >
              ยกเลิก
            </Button>
            <Button
              onClick={() => handleDelete(deleteConfirm)}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            >
              ลบเที่ยว
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
